import { useEffect, useState } from "react";
import { Plus, Trash2, Save, CreditCard, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BUILTINS = [
  { key: "phone", label: "Phone number" },
  { key: "table", label: "Table number (dine-in)" },
  { key: "pickup_time", label: "Preferred pickup time" },
  { key: "allergies", label: "Allergies / dietary notes" },
  { key: "tip", label: "Tip selector" },
];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function OrderFormBuilder() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/restaurant").then((res) => {
      const data = res.data;
      if (!data.order_form) data.order_form = { builtins: { phone: true, table: true, pickup_time: true, allergies: true, tip: true }, custom_fields: [] };
      if (!data.order_form.builtins) data.order_form.builtins = {};
      if (!data.order_form.custom_fields) data.order_form.custom_fields = [];
      setR(data);
    });
  }, []);

  if (!r) return <Loading />;
  const form = r.order_form;
  const setForm = (f) => setR({ ...r, order_form: f });

  const toggleBuiltin = (k, v) => setForm({ ...form, builtins: { ...form.builtins, [k]: v } });
  const addField = () => setForm({ ...form, custom_fields: [...form.custom_fields, { id: uid(), label: "New question", type: "text", required: false, options: [] }] });
  const updateField = (id, patch) => setForm({ ...form, custom_fields: form.custom_fields.map((f) => f.id === id ? { ...f, ...patch } : f) });
  const removeField = (id) => setForm({ ...form, custom_fields: form.custom_fields.filter((f) => f.id !== id) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/restaurant", { order_form: form, online_payment: !!r.online_payment });
      toast.success("Order form saved");
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <div data-testid="order-form-page">
      <PageHeader title="Online Order Form" subtitle="Choose what to ask customers at checkout — and whether they pay online."
        action={<Button data-testid="order-form-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Save"}</Button>} />

      <div className="bg-card border border-border rounded-xl p-6 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center"><CreditCard className="h-5 w-5 text-primary" /></span>
          <div>
            <p className="font-medium">Collect payment online</p>
            <p className="text-sm text-muted-foreground">When on, customers pay by card (Stripe) before the order reaches your kitchen.</p>
          </div>
        </div>
        <Switch data-testid="toggle-online-payment" checked={!!r.online_payment} onCheckedChange={(v) => setR({ ...r, online_payment: v })} />
      </div>

      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Built-in questions</h3>
        <div className="space-y-3">
          {BUILTINS.map((b) => (
            <div key={b.key} className="flex items-center justify-between py-1">
              <span className="text-sm">{b.label}</span>
              <Switch data-testid={`builtin-${b.key}`} checked={form.builtins[b.key] !== false} onCheckedChange={(v) => toggleBuiltin(b.key, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Custom questions</h3>
          <Button size="sm" variant="outline" onClick={addField} data-testid="add-custom-field" className="border-border hover:bg-muted"><Plus className="h-4 w-4 mr-1" /> Add question</Button>
        </div>
        {form.custom_fields.length === 0 ? (
          <p className="text-sm text-muted-foreground">No custom questions yet. Add your own (e.g. "How did you hear about us?").</p>
        ) : (
          <div className="space-y-4">
            {form.custom_fields.map((f) => (
              <div key={f.id} data-testid={`custom-field-${f.id}`} className="border border-border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <GripVertical className="h-5 w-5 text-muted-foreground mt-2 shrink-0" />
                  <div className="flex-1 space-y-3">
                    <Input value={f.label} onChange={(e) => updateField(f.id, { label: e.target.value })} placeholder="Question label" />
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={f.type} onValueChange={(v) => updateField(f.id, { type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Short text</SelectItem>
                          <SelectItem value="textarea">Long text</SelectItem>
                          <SelectItem value="dropdown">Dropdown</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2 justify-end">
                        <span className="text-sm text-muted-foreground">Required</span>
                        <Switch checked={f.required} onCheckedChange={(v) => updateField(f.id, { required: v })} />
                      </div>
                    </div>
                    {f.type === "dropdown" && (
                      <Input value={(f.options || []).join(", ")} onChange={(e) => updateField(f.id, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        placeholder="Options, comma separated: Google, Instagram, Friend" />
                    )}
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeField(f.id)} data-testid={`remove-field-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
